import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  CardActionArea,
  LinearProgress,
  Alert,
  Tooltip,
  CircularProgress,
  Skeleton,
  InputAdornment,
  Collapse,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  PlayCircleFilled as PlayIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  VideoLibrary as VideoLibraryIcon,
  CloudDownload as DownloadToCloudIcon,
  UploadFile as UploadIcon,
  DragIndicator as DragIcon,
  AutoAwesome as AutoFetchIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CreateNewFolder as NewLevelIcon,
  FolderOpen as LevelIcon,
  PlaylistAdd as AddUnitIcon,
  Folder as UnitIcon,
  SwapVert as MoveIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import useStore from '../store/useStore';
import { translations } from '../translations';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getRutubeId(url) {
  const m = url?.match(/rutube\.ru\/video\/([a-zA-Z0-9]+)/);
  return m ? m[1] : null;
}
function getYoutubeId(url) {
  const m = url?.match(/(?:youtu\.be\/|v=|embed\/)([^#&?]{11})/);
  return m ? m[1] : null;
}
function getVkVideoId(url) {
  // matches: vk.com/video-12345_67890 or vkvideo.ru/video-12345_67890
  const m = url?.match(/(?:vk\.com|vkvideo\.ru)\/video(-?\d+_\d+)/);
  return m ? m[1] : null;
}
function detectPlatform(url) {
  if (!url) return 'other';
  if (/rutube\.ru/.test(url)) return 'rutube';
  if (/youtu/.test(url)) return 'youtube';
  if (/vk\.com\/video|vkvideo\.ru/.test(url)) return 'vk';
  return 'other';
}

function VideoThumbnail({ video, size = 140 }) {
  const platform = video.platform || detectPlatform(video.url);
  const ytId = getYoutubeId(video.url);
  let thumb = video.thumbnailUrl || '';
  if (!thumb && platform === 'youtube' && ytId)
    thumb = `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`;

  return (
    <Box
      sx={{
        position: 'relative',
        height: size,
        bgcolor: '#111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {thumb ? (
        <img
          src={thumb}
          alt="thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.75,
          }}
        />
      ) : (
        <VideoLibraryIcon sx={{ fontSize: 48, color: 'grey.600' }} />
      )}
      <PlayIcon
        sx={{
          position: 'absolute',
          fontSize: 44,
          color: 'white',
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.6))',
        }}
      />
      <Chip
        label={
          platform === 'rutube'
            ? 'RuTube'
            : platform === 'youtube'
              ? 'YouTube'
              : platform === 'vk'
                ? 'VK Video'
                : 'Video'
        }
        size="small"
        sx={{
          position: 'absolute',
          bottom: 6,
          left: 6,
          bgcolor:
            platform === 'rutube'
              ? '#ff4b00'
              : platform === 'youtube'
                ? '#ff0000'
                : platform === 'vk'
                  ? '#0077ff'
                  : '#555',
          color: 'white',
          fontWeight: 700,
          fontSize: '0.6rem',
          height: 18,
        }}
      />
    </Box>
  );
}

function VideoEmbed({ video }) {
  const platform = video.platform || detectPlatform(video.url);
  const ytId = getYoutubeId(video.url);
  const rtId = getRutubeId(video.url);
  const vkId = getVkVideoId(video.url);

  if (platform === 'rutube' && rtId) {
    return (
      <iframe
        width="100%"
        height="100%"
        src={`https://rutube.ru/play/embed/${rtId}`}
        frameBorder="0"
        allow="clipboard-write; autoplay"
        allowFullScreen
        title={video.title}
      />
    );
  }
  if (platform === 'youtube' && ytId) {
    return (
      <iframe
        width="100%"
        height="100%"
        src={`https://www.youtube.com/embed/${ytId}?rel=0&autoplay=1`}
        title={video.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    );
  }
  if (platform === 'vk' && vkId) {
    // VK Video embed: https://vk.com/video_ext.php?oid=<ownerId>&id=<videoId>&hd=2
    const [ownerId, videoId] = vkId.split('_');
    return (
      <iframe
        width="100%"
        height="100%"
        src={`https://vk.com/video_ext.php?oid=${ownerId}&id=${videoId}&hd=2`}
        title={video.title}
        frameBorder="0"
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Typography color="grey.400">Unsupported video URL</Typography>
    </Box>
  );
}

// ─── Inline editable label ────────────────────────────────────────────────────
function InlineEdit({ value, onSave, sx }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  const commit = () => {
    if (val.trim() && val.trim() !== value) onSave(val.trim());
    setEditing(false);
  };

  if (editing) {
    return (
      <TextField
        size="small"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        sx={{ ...sx, minWidth: 140 }}
        inputProps={{ style: { fontWeight: 700 } }}
      />
    );
  }
  return (
    <Typography
      sx={{
        cursor: 'pointer',
        fontWeight: 700,
        ...sx,
        '&:hover': { textDecoration: 'underline dotted' },
      }}
      onDoubleClick={() => {
        setVal(value);
        setEditing(true);
      }}
    >
      {value}
    </Typography>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VideoLibrary() {
  const [structure, setStructure] = useState(null); // { _id, levels: [{name, units:[{name}]}] }
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Collapse state for levels/units  key = "level" or "level::unit"
  const [collapsed, setCollapsed] = useState({});

  // Video player
  const [activeVideo, setActiveVideo] = useState(null);

  // Add/edit video dialog
  const [videoDialog, setVideoDialog] = useState(null); // null | { mode:'add'|'edit', video, level, unit }
  const [metaLoading, setMetaLoading] = useState(false);

  // Move video dialog
  const [moveDialog, setMoveDialog] = useState(null); // null | { video }
  const [moveDest, setMoveDest] = useState({ level: '', unit: '' });

  // Download progress
  const [downloadProgress, setDownloadProgress] = useState({});

  // Drag state (for videos within a unit)
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [dragType, setDragType] = useState(null); // 'video' | 'level' | 'unit'
  const [draggingLevel, setDraggingLevel] = useState(null);
  const [dragOverLevel, setDragOverLevel] = useState(null);
  const [draggingUnit, setDraggingUnit] = useState(null); // {levelName, unitName}
  const [dragOverUnit, setDragOverUnit] = useState(null);

  // Import TXT
  const [openImport, setOpenImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const { userRole, language, socket } = useStore();
  const en = language === 'en';
  const isAdmin = userRole === 'admin';

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [structRes, vidRes] = await Promise.all([
      fetch('/api/admin/videos/structure', { credentials: 'include' }),
      fetch('/api/admin/videos', { credentials: 'include' }),
    ]);
    const [structData, vidData] = await Promise.all([
      structRes.json(),
      vidRes.json(),
    ]);
    setStructure(structData);
    setVideos(Array.isArray(vidData) ? vidData : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!socket) return;
    const onProg = ({ url, percent }) =>
      setDownloadProgress((p) => ({ ...p, [url]: percent }));
    const onDone = ({ url }) =>
      setDownloadProgress((p) => {
        const s = { ...p };
        delete s[url];
        return s;
      });
    const onErr = ({ url, error }) => {
      setDownloadProgress((p) => {
        const s = { ...p };
        delete s[url];
        return s;
      });
      alert(error);
    };
    socket.on('download_progress', onProg);
    socket.on('download_complete', onDone);
    socket.on('download_error', onErr);
    return () => {
      socket.off('download_progress', onProg);
      socket.off('download_complete', onDone);
      socket.off('download_error', onErr);
    };
  }, [socket]);

  // ── Structure mutations ───────────────────────────────────────────────────────
  const saveStructure = async (newLevels) => {
    const updated = { ...structure, levels: newLevels };
    setStructure(updated);
    await fetch('/api/admin/videos/structure', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ levels: newLevels }),
      credentials: 'include',
    });
  };

  const addLevel = async (name) => {
    if (!name.trim()) return;
    const levels = [
      ...(structure?.levels || []),
      { name: name.trim(), units: [] },
    ];
    await saveStructure(levels);
  };

  const addUnit = async (levelName, unitName) => {
    if (!unitName.trim()) return;
    const levels = (structure?.levels || []).map((l) =>
      l.name === levelName
        ? { ...l, units: [...l.units, { name: unitName.trim() }] }
        : l
    );
    await saveStructure(levels);
  };

  const renameLevel = async (oldName, newName) => {
    await fetch('/api/admin/videos/rename-section', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'level', oldName, newName }),
      credentials: 'include',
    });
    await fetchAll();
  };

  const renameUnit = async (parentLevel, oldName, newName) => {
    await fetch('/api/admin/videos/rename-section', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'unit', oldName, newName, parentLevel }),
      credentials: 'include',
    });
    await fetchAll();
  };

  const deleteSection = async (type, name, parentLevel) => {
    const count =
      type === 'level'
        ? videos.filter((v) => v.level === name).length
        : videos.filter(
            (v) => v.unit === name && (!parentLevel || v.level === parentLevel)
          ).length;
    const msg =
      count > 0
        ? `Delete "${name}" and its ${count} video${count !== 1 ? 's' : ''}? This cannot be undone.`
        : `Delete "${name}"?`;
    if (!window.confirm(msg)) return;
    await fetch('/api/admin/videos/section', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, name, parentLevel }),
      credentials: 'include',
    });
    await fetchAll();
  };

  // ── Video mutations ───────────────────────────────────────────────────────────
  const fetchMeta = async (url) => {
    if (
      !url ||
      (!url.includes('youtu') &&
        !url.includes('rutube') &&
        !url.includes('vk.com/video') &&
        !url.includes('vkvideo.ru'))
    )
      return;
    setMetaLoading(true);
    try {
      const res = await fetch(
        `/api/admin/videos/fetch-meta?url=${encodeURIComponent(url)}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const meta = await res.json();
        setVideoDialog((prev) => ({
          ...prev,
          video: {
            ...prev.video,
            title: prev.video.title || meta.title || '',
            thumbnailUrl: prev.video.thumbnailUrl || meta.thumbnailUrl || '',
            platform: meta.platform || 'other',
          },
        }));
      }
    } finally {
      setMetaLoading(false);
    }
  };

  const saveVideo = async () => {
    if (!videoDialog) return;
    const { mode, video } = videoDialog;
    const method = mode === 'edit' ? 'PATCH' : 'POST';
    const url =
      mode === 'edit' ? `/api/admin/videos/${video._id}` : '/api/admin/videos';
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(video),
      credentials: 'include',
    });

    // Ensure level/unit exist in structure
    if (mode === 'add') {
      const levels = structure?.levels || [];
      let newLevels = [...levels];
      let lvl = newLevels.find((l) => l.name === video.level);
      if (!lvl) {
        lvl = { name: video.level, units: [] };
        newLevels.push(lvl);
      }
      if (!lvl.units.find((u) => u.name === video.unit)) {
        newLevels = newLevels.map((l) =>
          l.name === video.level
            ? { ...l, units: [...l.units, { name: video.unit }] }
            : l
        );
        await saveStructure(newLevels);
      }
    }

    setVideoDialog(null);
    fetchAll();
  };

  const deleteVideo = async (id) => {
    if (!window.confirm(en ? 'Delete this video?' : 'Удалить видео?')) return;
    await fetch(`/api/admin/videos/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    fetchAll();
  };

  const moveVideo = async () => {
    if (!moveDialog || !moveDest.level || !moveDest.unit) return;
    await fetch(`/api/admin/videos/${moveDialog.video._id}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(moveDest),
      credentials: 'include',
    });
    setMoveDialog(null);
    fetchAll();
  };

  // ── Drag: videos within unit ──────────────────────────────────────────────────
  const handleVideoDrop = async (targetVideo) => {
    if (!dragging || dragging._id === targetVideo._id) return;
    if (
      dragging.level !== targetVideo.level ||
      dragging.unit !== targetVideo.unit
    )
      return;
    const section = videos
      .filter((v) => v.level === dragging.level && v.unit === dragging.unit)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const fromIdx = section.findIndex((v) => v._id === dragging._id);
    const toIdx = section.findIndex((v) => v._id === targetVideo._id);
    const reordered = [...section];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const orderedIds = reordered.map((v, i) => ({ id: v._id, order: i }));
    setDragging(null);
    setDragOver(null);
    setVideos((prev) => {
      const other = prev.filter(
        (v) => !(v.level === dragging.level && v.unit === dragging.unit)
      );
      return [...other, ...reordered.map((v, i) => ({ ...v, order: i }))];
    });
    await fetch('/api/admin/videos/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds }),
      credentials: 'include',
    });
  };

  // ── Drag: reorder levels ──────────────────────────────────────────────────────
  const handleLevelDrop = async (targetLevelName) => {
    if (!draggingLevel || draggingLevel === targetLevelName) return;
    const levels = structure?.levels || [];
    const fromIdx = levels.findIndex((l) => l.name === draggingLevel);
    const toIdx = levels.findIndex((l) => l.name === targetLevelName);
    const reordered = [...levels];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setDraggingLevel(null);
    setDragOverLevel(null);
    await saveStructure(reordered);
  };

  // ── Drag: reorder units within a level ────────────────────────────────────────
  const handleUnitDrop = async (targetUnit) => {
    if (!draggingUnit || draggingUnit.unitName === targetUnit) return;
    const levels = structure?.levels || [];
    const lvl = levels.find((l) => l.name === draggingUnit.levelName);
    if (!lvl) return;
    const fromIdx = lvl.units.findIndex(
      (u) => u.name === draggingUnit.unitName
    );
    const toIdx = lvl.units.findIndex((u) => u.name === targetUnit);
    const reorderedUnits = [...lvl.units];
    const [moved] = reorderedUnits.splice(fromIdx, 1);
    reorderedUnits.splice(toIdx, 0, moved);
    const newLevels = levels.map((l) =>
      l.name === draggingUnit.levelName ? { ...l, units: reorderedUnits } : l
    );
    setDraggingUnit(null);
    setDragOverUnit(null);
    await saveStructure(newLevels);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const toggleCollapse = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const getUnitVideos = (level, unit) =>
    videos
      .filter((v) => v.level === level && v.unit === unit)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const matchesSearch = (v) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      v.title?.toLowerCase().includes(q) ||
      v.level?.toLowerCase().includes(q) ||
      v.unit?.toLowerCase().includes(q)
    );
  };

  const allLevelNames = (structure?.levels || []).map((l) => l.name);
  const allUnitNames = (structure?.levels || []).flatMap((l) =>
    l.units.map((u) => ({ level: l.name, unit: u.name }))
  );

  if (loading) {
    return (
      <Layout title={en ? 'Videos' : 'Видео'}>
        <Container maxWidth="xl">
          {[1, 2].map((i) => (
            <Box key={i} sx={{ mb: 4 }}>
              <Skeleton
                variant="rounded"
                height={48}
                sx={{ mb: 2, borderRadius: 2 }}
              />
              {[1, 2, 3].map((j) => (
                <Skeleton
                  key={j}
                  variant="rounded"
                  height={200}
                  sx={{ mb: 2, ml: 3, borderRadius: 2 }}
                />
              ))}
            </Box>
          ))}
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={en ? 'Videos' : 'Видео'}>
      <Container maxWidth="xl">
        {/* ── Header ── */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 900, color: 'primary.main', mb: 0.5 }}
            >
              {en ? 'Video Library' : 'Видеотека'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {en
                ? `${videos.length} video${videos.length !== 1 ? 's' : ''} · YouTube & RuTube`
                : `${videos.length} видео · YouTube и RuTube`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {isAdmin && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => setOpenImport(true)}
                >
                  {en ? 'Import TXT' : 'Импорт TXT'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<NewLevelIcon />}
                  onClick={() => {
                    const name = prompt(
                      en
                        ? 'New level name (e.g. Level 3):'
                        : 'Название нового уровня:'
                    );
                    if (name) addLevel(name);
                  }}
                >
                  {en ? 'Add Level' : 'Добавить уровень'}
                </Button>
              </>
            )}
          </Stack>
        </Box>

        {/* ── Search ── */}
        <TextField
          fullWidth
          size="small"
          placeholder={en ? 'Search videos…' : 'Поиск видео…'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, maxWidth: 420 }}
        />

        {!structure?.levels?.length && (
          <Alert severity="info" sx={{ borderRadius: 3, mb: 3 }}>
            {en
              ? 'No levels yet. Click "Add Level" to get started.'
              : 'Уровней нет. Нажмите "Добавить уровень".'}
          </Alert>
        )}

        {/* ══ TREE ══════════════════════════════════════════════════════════════ */}
        {(structure?.levels || []).map((level, lIdx) => {
          const levelCollapsed = collapsed[level.name];
          const levelDragOver =
            dragOverLevel === level.name && draggingLevel !== level.name;

          return (
            <Paper
              key={level.name}
              variant="outlined"
              draggable={isAdmin}
              onDragStart={(e) => {
                e.stopPropagation();
                setDraggingLevel(level.name);
                setDragType('level');
              }}
              onDragEnd={() => {
                setDraggingLevel(null);
                setDragOverLevel(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dragType === 'level') setDragOverLevel(level.name);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget))
                  setDragOverLevel(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dragType === 'level') handleLevelDrop(level.name);
              }}
              sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                opacity: draggingLevel === level.name ? 0.4 : 1,
                outline: levelDragOver ? '2px dashed #C2233E' : 'none',
                transition: 'opacity 0.15s, outline 0.1s',
              }}
            >
              {/* Level header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  bgcolor: 'primary.main',
                  color: 'white',
                  gap: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => toggleCollapse(level.name)}
              >
                {isAdmin && (
                  <DragIcon
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      cursor: 'grab',
                      flexShrink: 0,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <LevelIcon
                  sx={{ color: 'rgba(255,255,255,0.8)', flexShrink: 0 }}
                />
                <Box
                  sx={{ flexGrow: 1, minWidth: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isAdmin ? (
                    <InlineEdit
                      value={level.name}
                      onSave={(v) => renameLevel(level.name, v)}
                      sx={{ color: 'white', fontSize: '1.1rem' }}
                    />
                  ) : (
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: 'white' }}
                    >
                      {level.name}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={`${level.units.length} ${en ? 'units' : 'тем'}`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                  }}
                />
                {isAdmin && (
                  <Tooltip
                    title={
                      en ? 'Add unit to this level' : 'Добавить тему в уровень'
                    }
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const name = prompt(
                          en
                            ? `New unit name in "${level.name}":`
                            : `Название новой темы в "${level.name}":`
                        );
                        if (name) addUnit(level.name, name);
                      }}
                    >
                      <AddUnitIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isAdmin && (
                  <Tooltip title={en ? 'Delete level' : 'Удалить уровень'}>
                    <IconButton
                      size="small"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': { color: '#fca5a5' },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSection('level', level.name);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {levelCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Box>

              <Collapse in={!levelCollapsed}>
                <Box sx={{ p: 2 }}>
                  {/* Units */}
                  {level.units.map((unit, uIdx) => {
                    const unitKey = `${level.name}::${unit.name}`;
                    const unitCollapsed = collapsed[unitKey];
                    const unitVideos = getUnitVideos(level.name, unit.name);
                    const filteredUnitVideos = searchQuery
                      ? unitVideos.filter(matchesSearch)
                      : unitVideos;
                    const unitDragOver =
                      dragOverUnit === unit.name &&
                      draggingUnit?.levelName === level.name &&
                      draggingUnit?.unitName !== unit.name;

                    return (
                      <Paper
                        key={unit.name}
                        variant="outlined"
                        draggable={isAdmin}
                        onDragStart={(e) => {
                          e.stopPropagation();
                          setDraggingUnit({
                            levelName: level.name,
                            unitName: unit.name,
                          });
                          setDragType('unit');
                        }}
                        onDragEnd={() => {
                          setDraggingUnit(null);
                          setDragOverUnit(null);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            dragType === 'unit' &&
                            draggingUnit?.levelName === level.name
                          )
                            setDragOverUnit(unit.name);
                        }}
                        onDragLeave={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget))
                            setDragOverUnit(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (dragType === 'unit') handleUnitDrop(unit.name);
                        }}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          opacity:
                            draggingUnit?.levelName === level.name &&
                            draggingUnit?.unitName === unit.name
                              ? 0.4
                              : 1,
                          outline: unitDragOver ? '2px dashed #7c3aed' : 'none',
                          transition: 'opacity 0.15s, outline 0.1s',
                        }}
                      >
                        {/* Unit header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                            bgcolor: 'grey.50',
                            gap: 1,
                            cursor: 'pointer',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                          onClick={() => toggleCollapse(unitKey)}
                        >
                          {isAdmin && (
                            <DragIcon
                              sx={{
                                color: 'grey.400',
                                cursor: 'grab',
                                fontSize: 18,
                                flexShrink: 0,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <UnitIcon
                            sx={{
                              color: 'secondary.main',
                              fontSize: 18,
                              flexShrink: 0,
                            }}
                          />
                          <Box
                            sx={{ flexGrow: 1, minWidth: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isAdmin ? (
                              <InlineEdit
                                value={unit.name}
                                onSave={(v) =>
                                  renameUnit(level.name, unit.name, v)
                                }
                                sx={{ color: 'secondary.main' }}
                              />
                            ) : (
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: 'secondary.main',
                                }}
                              >
                                {unit.name}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={`${filteredUnitVideos.length}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 700,
                              height: 20,
                              fontSize: '0.65rem',
                            }}
                          />
                          {isAdmin && (
                            <Tooltip
                              title={
                                en ? 'Add video here' : 'Добавить видео сюда'
                              }
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVideoDialog({
                                    mode: 'add',
                                    video: {
                                      title: '',
                                      url: '',
                                      level: level.name,
                                      unit: unit.name,
                                      thumbnailUrl: '',
                                      platform: 'youtube',
                                    },
                                    level: level.name,
                                    unit: unit.name,
                                  });
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip
                              title={en ? 'Delete unit' : 'Удалить тему'}
                            >
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSection('unit', unit.name, level.name);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {unitCollapsed ? (
                            <ExpandMoreIcon fontSize="small" />
                          ) : (
                            <ExpandLessIcon fontSize="small" />
                          )}
                        </Box>

                        <Collapse in={!unitCollapsed}>
                          <Box sx={{ p: 2 }}>
                            {filteredUnitVideos.length === 0 ? (
                              <Box
                                sx={{
                                  textAlign: 'center',
                                  py: 3,
                                  color: 'text.disabled',
                                }}
                              >
                                <VideoLibraryIcon
                                  sx={{ fontSize: 40, mb: 1, opacity: 0.3 }}
                                />
                                <Typography variant="body2">
                                  {searchQuery
                                    ? en
                                      ? 'No matching videos'
                                      : 'Нет совпадений'
                                    : en
                                      ? 'No videos yet — click + to add one'
                                      : 'Видео нет — нажмите + чтобы добавить'}
                                </Typography>
                                {isAdmin && !searchQuery && (
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    sx={{ mt: 1 }}
                                    onClick={() =>
                                      setVideoDialog({
                                        mode: 'add',
                                        video: {
                                          title: '',
                                          url: '',
                                          level: level.name,
                                          unit: unit.name,
                                          thumbnailUrl: '',
                                          platform: 'youtube',
                                        },
                                        level: level.name,
                                        unit: unit.name,
                                      })
                                    }
                                  >
                                    {en
                                      ? 'Add first video'
                                      : 'Добавить первое видео'}
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Grid container spacing={2}>
                                {filteredUnitVideos.map((video) => (
                                  <Grid
                                    size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                    key={video._id}
                                  >
                                    <Card
                                      draggable={isAdmin}
                                      onDragStart={(e) => {
                                        e.stopPropagation();
                                        setDragging(video);
                                        setDragType('video');
                                      }}
                                      onDragEnd={() => {
                                        setDragging(null);
                                        setDragOver(null);
                                      }}
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (dragType === 'video')
                                          setDragOver(video._id);
                                      }}
                                      onDragLeave={() => setDragOver(null)}
                                      onDrop={(e) => {
                                        e.stopPropagation();
                                        if (dragType === 'video')
                                          handleVideoDrop(video);
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        position: 'relative',
                                        opacity:
                                          dragging?._id === video._id ? 0.4 : 1,
                                        outline:
                                          dragOver === video._id
                                            ? '2px dashed #C2233E'
                                            : 'none',
                                        transition:
                                          'opacity 0.15s, outline 0.1s',
                                      }}
                                    >
                                      <CardActionArea
                                        onClick={() => setActiveVideo(video)}
                                      >
                                        <VideoThumbnail video={video} />
                                        <CardContent
                                          sx={{
                                            pb: '12px !important',
                                            pt: 1.5,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{
                                              fontWeight: 700,
                                              lineHeight: 1.3,
                                            }}
                                            noWrap
                                          >
                                            {video.title}
                                          </Typography>
                                          {downloadProgress[video.url] !==
                                            undefined && (
                                            <Box sx={{ mt: 0.5 }}>
                                              <LinearProgress
                                                variant="determinate"
                                                value={Number(
                                                  downloadProgress[video.url]
                                                )}
                                              />
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                {Math.round(
                                                  downloadProgress[video.url]
                                                )}
                                                %
                                              </Typography>
                                            </Box>
                                          )}
                                        </CardContent>
                                      </CardActionArea>

                                      {/* Drag handle */}
                                      {isAdmin && (
                                        <DragIcon
                                          sx={{
                                            position: 'absolute',
                                            top: 6,
                                            left: 6,
                                            color: 'white',
                                            cursor: 'grab',
                                            filter:
                                              'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                                          }}
                                        />
                                      )}

                                      {/* Admin actions */}
                                      {isAdmin && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            display: 'flex',
                                            gap: 0.5,
                                          }}
                                        >
                                          <Tooltip
                                            title={
                                              en
                                                ? 'Download to Resources'
                                                : 'Скачать'
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              sx={{
                                                bgcolor:
                                                  'rgba(255,255,255,0.85)',
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                fetch(
                                                  '/api/admin/videos/download',
                                                  {
                                                    method: 'POST',
                                                    headers: {
                                                      'Content-Type':
                                                        'application/json',
                                                    },
                                                    body: JSON.stringify({
                                                      url: video.url,
                                                      title: video.title,
                                                    }),
                                                    credentials: 'include',
                                                  }
                                                );
                                                setDownloadProgress((p) => ({
                                                  ...p,
                                                  [video.url]: 0,
                                                }));
                                              }}
                                            >
                                              <DownloadToCloudIcon
                                                fontSize="inherit"
                                                color="primary"
                                              />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip
                                            title={
                                              en
                                                ? 'Move to different section'
                                                : 'Переместить'
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              sx={{
                                                bgcolor:
                                                  'rgba(255,255,255,0.85)',
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setMoveDest({
                                                  level: video.level,
                                                  unit: video.unit,
                                                });
                                                setMoveDialog({ video });
                                              }}
                                            >
                                              <MoveIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip
                                            title={
                                              en ? 'Edit' : 'Редактировать'
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              sx={{
                                                bgcolor:
                                                  'rgba(255,255,255,0.85)',
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setVideoDialog({
                                                  mode: 'edit',
                                                  video: { ...video },
                                                  level: video.level,
                                                  unit: video.unit,
                                                });
                                              }}
                                            >
                                              <EditIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip
                                            title={en ? 'Delete' : 'Удалить'}
                                          >
                                            <IconButton
                                              size="small"
                                              color="error"
                                              sx={{
                                                bgcolor:
                                                  'rgba(255,255,255,0.85)',
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                deleteVideo(video._id);
                                              }}
                                            >
                                              <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}

                  {/* Add unit button at bottom of level */}
                  {isAdmin && (
                    <Button
                      size="small"
                      startIcon={<AddUnitIcon />}
                      variant="outlined"
                      sx={{ borderRadius: 2, borderStyle: 'dashed', mt: 1 }}
                      onClick={() => {
                        const name = prompt(
                          en
                            ? `New unit in "${level.name}":`
                            : `Новая тема в "${level.name}":`
                        );
                        if (name) addUnit(level.name, name);
                      }}
                    >
                      {en
                        ? `Add unit to ${level.name}`
                        : `Добавить тему в ${level.name}`}
                    </Button>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}

        {/* Add Level button at bottom */}
        {isAdmin && (structure?.levels?.length ?? 0) > 0 && (
          <Button
            variant="outlined"
            startIcon={<NewLevelIcon />}
            sx={{ borderRadius: 2, borderStyle: 'dashed', mb: 4 }}
            onClick={() => {
              const name = prompt(
                en ? 'New level name:' : 'Название нового уровня:'
              );
              if (name) addLevel(name);
            }}
          >
            {en ? 'Add another Level' : 'Добавить уровень'}
          </Button>
        )}

        {/* ─── Video Player Dialog ─────────────────────────────────────────── */}
        <Dialog
          open={Boolean(activeVideo)}
          onClose={() => setActiveVideo(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              bgcolor: 'primary.main',
              color: 'white',
              py: 1.5,
            }}
          >
            {activeVideo?.title}
          </DialogTitle>
          <DialogContent sx={{ p: 0, bgcolor: 'black', height: 460 }}>
            {activeVideo && <VideoEmbed video={activeVideo} />}
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'grey.900' }}>
            <Button
              onClick={() => setActiveVideo(null)}
              sx={{ color: 'white' }}
            >
              {en ? 'Close' : 'Закрыть'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ─── Add / Edit Video Dialog ─────────────────────────────────────── */}
        <Dialog
          open={Boolean(videoDialog)}
          onClose={() => setVideoDialog(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            {videoDialog?.mode === 'edit'
              ? en
                ? 'Edit Video'
                : 'Редактировать видео'
              : en
                ? `Add Video → ${videoDialog?.level} / ${videoDialog?.unit}`
                : `Добавить видео → ${videoDialog?.level} / ${videoDialog?.unit}`}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {/* Locked level/unit labels */}
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<LevelIcon />}
                  label={videoDialog?.video?.level || '—'}
                  color="primary"
                />
                <Chip
                  icon={<UnitIcon />}
                  label={videoDialog?.video?.unit || '—'}
                  color="secondary"
                />
              </Stack>

              {/* URL with auto-fetch */}
              <TextField
                fullWidth
                label={
                  en ? 'YouTube or RuTube URL' : 'Ссылка YouTube или RuTube'
                }
                placeholder="YouTube, RuTube, VK Video URL"
                value={videoDialog?.video?.url || ''}
                onChange={(e) =>
                  setVideoDialog((p) => ({
                    ...p,
                    video: { ...p.video, url: e.target.value },
                  }))
                }
                onBlur={(e) => fetchMeta(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {metaLoading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Tooltip
                          title={
                            en ? 'Fetch title & thumbnail' : 'Получить данные'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => fetchMeta(videoDialog?.video?.url)}
                          >
                            <AutoFetchIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Thumbnail preview */}
              {videoDialog?.video?.url && (
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    height: 100,
                    bgcolor: '#111',
                  }}
                >
                  <VideoThumbnail
                    video={{
                      ...videoDialog.video,
                      platform:
                        videoDialog.video.platform ||
                        detectPlatform(videoDialog.video.url),
                    }}
                    size={100}
                  />
                </Box>
              )}

              <TextField
                fullWidth
                label={en ? 'Title' : 'Название'}
                value={videoDialog?.video?.title || ''}
                onChange={(e) =>
                  setVideoDialog((p) => ({
                    ...p,
                    video: { ...p.video, title: e.target.value },
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setVideoDialog(null)}>
              {en ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              variant="contained"
              onClick={saveVideo}
              disabled={!videoDialog?.video?.url}
            >
              {en ? 'Save' : 'Сохранить'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ─── Move Video Dialog ────────────────────────────────────────────── */}
        <Dialog
          open={Boolean(moveDialog)}
          onClose={() => setMoveDialog(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {en ? 'Move Video' : 'Переместить видео'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {moveDialog?.video?.title}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{en ? 'Level' : 'Уровень'}</InputLabel>
              <Select
                value={moveDest.level}
                label={en ? 'Level' : 'Уровень'}
                onChange={(e) =>
                  setMoveDest({ level: e.target.value, unit: '' })
                }
              >
                {allLevelNames.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{en ? 'Unit' : 'Тема'}</InputLabel>
              <Select
                value={moveDest.unit}
                label={en ? 'Unit' : 'Тема'}
                disabled={!moveDest.level}
                onChange={(e) =>
                  setMoveDest((p) => ({ ...p, unit: e.target.value }))
                }
              >
                {(
                  structure?.levels?.find((l) => l.name === moveDest.level)
                    ?.units || []
                ).map((u) => (
                  <MenuItem key={u.name} value={u.name}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMoveDialog(null)}>
              {en ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              variant="contained"
              onClick={moveVideo}
              disabled={!moveDest.level || !moveDest.unit}
              startIcon={<CheckIcon />}
            >
              {en ? 'Move' : 'Переместить'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ─── Import TXT Dialog ──────────────────────────────────────────── */}
        <Dialog
          open={openImport}
          onClose={() => setOpenImport(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {en ? 'Import Videos from TXT' : 'Импорт из TXT'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {en
                ? 'Upload a .txt file. Lines with URLs become videos. Other lines are used as Level/Unit headings.'
                : 'Строки со ссылками — видео, остальные — заголовки уровней/тем.'}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              {importFile
                ? importFile.name
                : en
                  ? 'Select .txt File'
                  : 'Выбрать .txt файл'}
              <input
                type="file"
                hidden
                accept=".txt"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenImport(false)}>
              {en ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              onClick={async () => {
                if (!importFile) return;
                setImportLoading(true);
                const fd = new FormData();
                fd.append('file', importFile);
                await fetch('/api/admin/videos/import-txt', {
                  method: 'POST',
                  body: fd,
                  credentials: 'include',
                });
                setOpenImport(false);
                setImportFile(null);
                setImportLoading(false);
                fetchAll();
              }}
              variant="contained"
              disabled={!importFile || importLoading}
            >
              {importLoading ? (
                <CircularProgress size={18} />
              ) : en ? (
                'Import'
              ) : (
                'Импортировать'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
}
